import { Component, defineElement } from '@theme/component';
import { attachRailIndicator, revealInRail } from '@theme/rail-indicator';

/** FAQ page category navigation. */

class FaqPage extends Component {
  ready() {
    this.tablist = this.querySelector('[data-faq-tablist]');
    this.rail = this.querySelector('[data-faq-rail]');
    this.nav = this.querySelector('[data-nav-stuck]');
    this.links = this.tablist
      ? [...this.tablist.querySelectorAll('[data-faq-link]')]
      : [];
    this.groups = this.links
      .map((link) => this.querySelector(`[data-faq-group="${CSS.escape(link.dataset.faqTarget)}"]`))
      .filter(Boolean);

    this.hoveredLink = null;
    this.focusedLink = null;
    this.finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.resizeTimer = 0;
    this.stuckObserver = null;
    this.stuckMargin = '';

    if (this.links.length < 2 || this.groups.length !== this.links.length) return;

    this.spy = window.DieuxScrollSpy?.create({
      name: 'faq-page',
      targets: this.groups,
      idOf: (group) => group.dataset.faqGroup,
      onCurrent: (id, changed) => this.applyCurrent(id, changed),
      tail: { host: this, property: '--faq-page-tail-room' },
      onSync: () => {
        this.syncStuck();
        this.scheduleArc();
      },
    });
    if (!this.spy) return;

    attachRailIndicator(this.rail, () => this.arcTarget());

    this.links.forEach((link) => {
      this.listen(link, 'click', () => this.spy.request(link.dataset.faqTarget));
    });

    this.listen(this, 'pointerover', (event) => {
      if (!this.finePointerQuery.matches || event.pointerType === 'touch') return;

      const link = event.target.closest('[data-faq-link]');
      if (!link || link.contains(event.relatedTarget)) return;

      this.hoveredLink = link;
      this.scheduleArc();
    });
    this.listen(this, 'pointerout', (event) => {
      if (this.tablist.contains(event.relatedTarget)) return;

      this.hoveredLink = null;
      this.scheduleArc();
    });
    this.listen(this, 'focusin', (event) => {
      const link = event.target.closest('[data-faq-link]');
      if (!link) return;

      this.focusedLink = link;
      this.scheduleArc();
      if (link.matches(':focus-visible')) this.revealLink(link);
    });
    this.listen(this, 'focusout', () => {
      queueMicrotask(() => {
        if (!this.isConnected) return;

        const active = document.activeElement?.closest?.('[data-faq-link]');
        this.focusedLink = active && this.contains(active) ? active : null;
        this.scheduleArc();
      });
    });
    this.resizeObserver = new ResizeObserver(() => this.scheduleReconcile());
    this.resizeObserver.observe(this);

    this.spy.start(
      this.spy.idFromHash() ||
        this.currentLink()?.dataset.faqTarget ||
        this.links[0].dataset.faqTarget
    );
    this.revealLink(this.currentLink(), { align: 'center', behavior: 'auto' });
    this.scheduleArc();
  }

  teardown() {
    this.spy?.destroy();
    this.spy = null;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.stuckObserver?.disconnect();
    this.stuckObserver = null;
    this.stuckMargin = '';
    attachRailIndicator(this.rail, null);
    this.hoveredLink = null;
    this.focusedLink = null;
    clearTimeout(this.resizeTimer);
    this.resizeTimer = 0;
  }

  currentLink() {
    return this.links.find((link) => link.getAttribute('aria-current') === 'location') || null;
  }

  linkFor(id) {
    return this.links.find((link) => link.dataset.faqTarget === id) || null;
  }

  /**
   * The link the customer is engaging with, which outranks the one their scroll
   * position resolved to.
   *
   * @returns {HTMLElement|null} Hovered link, else focused link, else null.
   */
  interactionArcTarget() {
    if (this.hoveredLink?.isConnected) return this.hoveredLink;
    return this.focusedLink?.isConnected ? this.focusedLink : null;
  }

  /**
   * Shows the category the tracker resolved.
   *
   * @param {string} id - Category anchor now being read.
   * @param {boolean} changed - Whether this is a different category than before.
   * @returns {void}
   */
  applyCurrent(id, changed) {
    const nextLink = this.linkFor(id);
    if (!nextLink) return;

    if (changed) {
      this.links.forEach((link) => {
        if (link === nextLink) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      this.scheduleArc();
    }

    this.revealLink(nextLink);
  }

  revealLink(link, options) {
    revealInRail(this.rail, link, options);
  }

  scheduleReconcile() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      this.resizeTimer = 0;
      if (!this.isConnected) return;

      this.spy?.sync();
    }, window.DieuxScrollSpy?.RESIZE_DEBOUNCE_MS ?? 150);
  }

  scheduleArc() {
    this.rail?.scheduleIndicator?.();
  }

  /**
   * @returns {boolean} Whether the navigation is currently a horizontal rail.
   */
  get horizontalNav() {
    return (
      getComputedStyle(this).getPropertyValue('--faq-page-nav-orientation').trim() === 'horizontal'
    );
  }

  /**
   * Reports whether the rail is pinned under the site header, which is what its
   * bottom border is drawn against.
   *
   * @returns {void}
   */
  syncStuck() {
    if (!this.nav) return;

    const styles = getComputedStyle(this.nav);
    const pinned = this.horizontalNav && styles.position === 'sticky';
    const line = pinned ? Number.parseFloat(styles.insetBlockStart) || 0 : 0;
    const margin = pinned ? `-${line + 1}px 0px 0px 0px` : '';

    if (margin === this.stuckMargin) return;

    this.stuckMargin = margin;
    this.stuckObserver?.disconnect();
    this.stuckObserver = null;
    this.nav.dataset.navStuck = 'false';
    if (!pinned) return;

    this.stuckObserver = new IntersectionObserver(
      ([entry]) => {
        this.nav.dataset.navStuck = String(entry.intersectionRatio < 1);
      },
      { rootMargin: margin, threshold: [1] }
    );
    this.stuckObserver.observe(this.nav);
  }

  /**
   * Resolves the link the shared rail indicator belongs under.
   *
   * @returns {HTMLElement|null} The link, or `null` while the navigation is a column.
   */
  arcTarget() {
    if (!this.horizontalNav) {
      this.rail?.releaseIndicator?.();
      return null;
    }

    return this.interactionArcTarget() || this.currentLink();
  }
}

defineElement('faq-page', FaqPage);
